import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePermissions, Permission } from '../../../shared/hooks/usePermissions';
import { Modal } from '../../../shared/components/Modal';
import Button from '../../../shared/components/Button';
import {
  useCharterAdminProvider,
  useCharterAdminProviderAddons,
  useCreateAdminProviderAddon,
  useUpdateAdminProviderAddon,
} from '../hooks/useCharter';
import { AddonForm } from '../components/AddonForm';
import { AddonSourceBadge, GatewayStateBadge } from '../components/CharterStatusBadge';
import { DetailField } from '../components/IntakeResponses';
import {
  CharterHeader,
  EmptyState,
  ErrorState,
  LoadingState,
  TableCard,
  TD,
  TD_MUTED,
  TH,
} from '../components/CharterPage';
import { formatPrice } from '../components/charterFormat';
import type { CharterAddon, CreateAddonPayload } from '../types';

/**
 * TCS-admin: a single provider's detail. Profile + gateway + cancellation policy are
 * read-only here (the provider edits their own). Add-ons CAN be created/edited on the
 * provider's behalf (source=TCS_ADMIN), shown alongside merchant-created ones by source.
 * Note: charter-api exposes no admin endpoint for a provider's services/packages — those
 * stay in the merchant back-office (see PR body).
 */
export function CharterProviderDetailPage() {
  const { merchantId = null } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.CHARTER_ADMIN);

  const providerQuery = useCharterAdminProvider(merchantId);
  const addonsQuery = useCharterAdminProviderAddons(merchantId);
  const createAddon = useCreateAdminProviderAddon();
  const updateAddon = useUpdateAdminProviderAddon();

  const provider = providerQuery.data;
  const addons = addonsQuery.data ?? [];

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CharterAddon | null>(null);

  const saving = createAddon.isPending || updateAddon.isPending;

  const submitAddon = async (values: CreateAddonPayload) => {
    if (!merchantId) return;
    if (editing) await updateAddon.mutateAsync({ merchantId, id: editing.id, data: values });
    else await createAddon.mutateAsync({ merchantId, data: values });
    setModalOpen(false);
    setEditing(null);
  };

  return (
    <div className="py-6 max-w-5xl mx-auto px-2 space-y-5">
      <button onClick={() => navigate('/charter/providers')} className="text-sm text-blue-600 hover:underline">
        &larr; Back to providers
      </button>

      {providerQuery.isLoading ? (
        <LoadingState label="Loading provider…" />
      ) : providerQuery.error || !provider ? (
        <ErrorState label="Failed to load provider." />
      ) : (
        <>
          <CharterHeader
            title={provider.branding?.displayName ?? 'Charter provider'}
            subtitle={provider.merchantId}
          />

          <div className="bg-card rounded-2xl border border-border p-6 grid grid-cols-2 gap-5">
            <DetailField label="Display name">{provider.branding?.displayName ?? '—'}</DetailField>
            <DetailField label="Tagline">{provider.branding?.tagline ?? '—'}</DetailField>
            <DetailField label="Timezone">{provider.timezone ?? '—'}</DetailField>
            <DetailField label="Gateway state">
              <GatewayStateBadge state={provider.gatewayState} />
            </DetailField>
            <div className="col-span-2">
              <DetailField label="Bio">{provider.branding?.bio ?? '—'}</DetailField>
            </div>
            <div className="col-span-2">
              <DetailField label="Cancellation policy">
                {provider.cancellationPolicy?.policyText ?? '—'}
                {provider.cancellationPolicy?.cancellationNoticeHours != null && (
                  <span className="block text-xs text-muted-foreground mt-1">
                    Cancel notice: {provider.cancellationPolicy.cancellationNoticeHours}h
                    {provider.cancellationPolicy.rescheduleNoticeHours != null
                      ? ` · Reschedule notice: ${provider.cancellationPolicy.rescheduleNoticeHours}h`
                      : ''}
                  </span>
                )}
              </DetailField>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <h2 className="text-[16px] font-semibold text-foreground">Add-ons</h2>
            {canManage && (
              <Button
                size="sm"
                className="h-[38px]"
                onClick={() => {
                  setEditing(null);
                  setModalOpen(true);
                }}
              >
                New add-on (TCS)
              </Button>
            )}
          </div>

          {addonsQuery.isLoading ? (
            <LoadingState label="Loading add-ons…" />
          ) : addons.length === 0 ? (
            <EmptyState label="No add-ons for this provider." />
          ) : (
            <TableCard>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted text-left">
                    <th className={TH}>Name</th>
                    <th className={TH}>Price</th>
                    <th className={TH}>Extra time</th>
                    <th className={TH}>Source</th>
                    {canManage && <th className={TH}>Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {addons.map((a) => (
                    <tr key={a.id} className="hover:bg-muted transition-colors">
                      <td className={`${TD} font-medium`}>{a.name}</td>
                      <td className={TD}>{formatPrice(a.price, a.currency)}</td>
                      <td className={TD_MUTED}>{a.extraDurationMinutes ? `+${a.extraDurationMinutes} min` : '—'}</td>
                      <td className="px-6 py-4">
                        <AddonSourceBadge source={a.source} />
                      </td>
                      {canManage && (
                        <td className="px-6 py-4">
                          <button
                            onClick={() => {
                              setEditing(a);
                              setModalOpen(true);
                            }}
                            className="text-xs text-blue-600 hover:underline"
                          >
                            Edit
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableCard>
          )}
        </>
      )}

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} size="md">
        <div className="p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-foreground">{editing ? 'Edit add-on' : 'New add-on (TCS)'}</h2>
            <button onClick={() => setModalOpen(false)} className="text-muted-foreground hover:text-foreground text-2xl leading-none">
              &times;
            </button>
          </div>
          <AddonForm
            initial={editing}
            packages={[]}
            onSubmit={submitAddon}
            onCancel={() => setModalOpen(false)}
            isSaving={saving}
          />
        </div>
      </Modal>
    </div>
  );
}
